import { PencilSquareIcon, PlusCircleIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'

import { yupResolver } from '@hookform/resolvers/yup'
import { format, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import { toast } from 'sonner'
import * as yup from 'yup'

import { ProfilePhotoImage } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import { primaryIbanOptions } from '@/constants'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { capitalizeFirstLetter, formatIban, normalizeIban } from '@/lib/helper'
import { Layout } from '@/pages/customer/layout'
import authService from '@/services/auth-service'
import userService from '@/services/user-service'
import { updateUser } from '@/slices/auth'
import { InputMask } from '@react-input/mask'

export interface UserForm {
	first_name: string
	last_name: string
	email: string
	phone_number: string
	address: string
	country: string
	zip: string
	iban: string
	reference_code: string
	referral_code: string
}

export const Profile = () => {
	const dispatch = useAppDispatch()
	const auth = useAppSelector(state => state.auth)

	const [isModalVisible, setIsModalVisible] = useState<number | null>(null)

	const { data: referralCount } = useQuery('fetch-referral-count', userService.fetchReferralCount)

	const { mutate: generateReferralCode } = useMutation({
		mutationKey: 'generate-referral-code',
		mutationFn: userService.generateReferralCode,
		onSuccess: res => {
			form.setValue('referral_code', res.data)
			dispatch(
				updateUser({
					...form.getValues(),
					iban: normalizeIban(form.getValues('iban')),
					referral_code: res.data,
				}),
			)
			toast.success(res.message)
		},
		onError: (error: any) => {
			toast.error(error?.response?.data ?? error?.request ?? error?.message)
		},
	})

	const { mutate: updateUserProfile } = useMutation({
		mutationKey: 'update-user-profile',
		mutationFn: userService.updateUserProfile,
		onSuccess: res => {
			dispatch(updateUser({ ...form.getValues(), iban: normalizeIban(form.getValues('iban')) }))
			toast.success(res.message)
			form.reset()
			setIsModalVisible(null)
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message ?? error?.request?.message ?? error?.message)
		},
	})

	const { mutate: updatePassword } = useMutation({
		mutationKey: 'update-password',
		mutationFn: authService.updatePassword,
		onSuccess: () => {
			toast.success('Password changed!')
			setIsModalVisible(null)
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message ?? error?.request?.message ?? error?.message)
		},
	})

	const passwordSchema = yup
		.object<{ email: string; newPassword: string; oldPassword: string }>()
		.shape({
			email: yup
				.string()
				.required('E-Mail ist erforderlich!')
				.email('Geben Sie eine gültige E-Mail-Adresse ein'),
			newPassword: yup.string().required('Neues passwort ist erforderlich!'),
			oldPassword: yup.string().required('Altes passwort ist erforderlich!'),
		})

	const schema = yup.object<UserForm>().shape({
		first_name: yup.string().required('Name ist erforderlich!'),
		last_name: yup.string().required('Name ist erforderlich!'),
		email: yup
			.string()
			.required('E-Mail ist erforderlich!')
			.email('Geben Sie eine gültige E-Mail-Adresse ein'),
		phone_number: yup.string().required('Telefonnummer ist erforderlich!'),
		address: yup.string().required('Adresse ist erforderlich!'),
		country: yup.string().required('Ort ist erforderlich!'),
		zip: yup.string().required('Postleitzahl ist erforderlich!'),
		iban: yup
			.string()
			.required('IBAN ist erforderlich!')
			.matches(/^DE(\s?\d){20}$/, "IBAN muss mit 'DE' beginnen und genau 20 Zahlen enthalten"),
		referral_code: yup.string().notRequired(),
		reference_code: yup.string().notRequired(),
	})

	const passwordForm = useForm<{ email: string; newPassword: string; oldPassword: string }>({
		defaultValues: { email: auth.email, oldPassword: '', newPassword: '' },
		resolver: yupResolver(passwordSchema),
		mode: 'all',
	})

	const form = useForm<UserForm>({
		defaultValues: {
			first_name: auth.first_name,
			last_name: auth.last_name,
			email: auth.email,
			phone_number: auth.phone_number ?? undefined,
			country: auth.country,
			address: auth.address ?? undefined,
			zip: auth.zip,
			iban: formatIban(auth.iban),
			reference_code: auth.reference_code ?? undefined,
			referral_code: auth.referral_code ?? undefined,
		},
		resolver: yupResolver(schema as any),
		mode: 'all',
	})

	const onSubmit = (data: UserForm) => {
		const payload = {
			...data,
			iban: normalizeIban(data.iban),
			old_data: {
				first_name: auth.first_name,
				last_name: auth.last_name,
				email: auth.email,
				phone_number: auth.phone_number ?? undefined,
				country: auth.country,
				address: auth.address ?? undefined,
				zip: auth.zip,
				iban: normalizeIban(auth.iban),
				reference_code: auth.reference_code ?? undefined,
				referral_code: auth.referral_code ?? undefined,
			},
		}
		updateUserProfile(payload)
	}

	const onPasswordSubmit = (data: { email: string; newPassword: string; oldPassword: string }) => {
		updatePassword(data)
	}

	return (
		<>
			{auth.type === 'admin' ? (
				<div className="container mx-auto md:py-5">
					<ResponsiveModal
						open={!!isModalVisible}
						onOpenChange={() => {
							form.reset()
							passwordForm.reset()
							form.clearErrors()
							passwordForm.clearErrors()
							setIsModalVisible(null)
						}}>
						<ResponsiveModalContent className="gap-0 !max-w-[550px]">
							<ResponsiveModalHeader className="max-lg:pb-2">
								<ResponsiveModalTitle className="mb-0 max-lg:text-left">
									{isModalVisible === 1
										? 'Persönliche Daten'
										: isModalVisible === 2
											? 'Adresse'
											: 'Passwort ändern'}
								</ResponsiveModalTitle>
								<ResponsiveModalDescription className="sr-only">
									Update profile
								</ResponsiveModalDescription>
							</ResponsiveModalHeader>
							{isModalVisible === 3 ? (
								<Form {...passwordForm}>
									<form
										onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
										className="flex flex-col gap-10">
										<div className="mt-10 flex flex-col gap-8">
											<FormField
												control={passwordForm.control}
												name="email"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">Email</FormLabel>
														<FormControl>
															<Input placeholder="Email" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
											<FormField
												control={passwordForm.control}
												name="oldPassword"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">Altes passwort</FormLabel>
														<FormControl>
															<Input placeholder="Altes passwort" type="password" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
											<FormField
												control={passwordForm.control}
												name="newPassword"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">Neues passwort</FormLabel>
														<FormControl>
															<Input placeholder="Neues passwort" type="password" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<Button variant="secondary" className="h-14 rounded-xl">
											Daten speichern
										</Button>
									</form>
								</Form>
							) : (
								<Form {...form}>
									<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-10">
										{isModalVisible === 1 ? (
											<div className="mt-10 flex flex-col gap-8">
												<FormField
													control={form.control}
													name="first_name"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Name</FormLabel>
															<FormControl>
																<Input
																	placeholder="Name"
																	{...field}
																	value={capitalizeFirstLetter(field.value)}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="last_name"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Name</FormLabel>
															<FormControl>
																<Input
																	placeholder="Name"
																	{...field}
																	value={capitalizeFirstLetter(field.value)}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="email"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Email</FormLabel>
															<FormControl>
																<Input disabled placeholder="Email" {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="phone_number"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Telefonnummer</FormLabel>
															<FormControl>
																<Input placeholder="Telefonnummer" {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										) : (
											<div className="mt-10 flex flex-col gap-8">
												<FormField
													control={form.control}
													name="zip"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Postleitzahl</FormLabel>
															<FormControl>
																<Input placeholder="Postleitzahl" {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="country"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Ort</FormLabel>
															<FormControl>
																<Input placeholder="Ort" {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="address"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Adresse</FormLabel>
															<FormControl>
																<Input
																	placeholder="Adresse"
																	{...field}
																	value={capitalizeFirstLetter(field.value)}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="iban"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">
																IBAN <span className="text-destructive-foreground">*</span>
															</FormLabel>
															<FormControl>
																<InputMask
																	{...primaryIbanOptions}
																	value={field.value ?? ''}
																	onChange={e => field.onChange(e.target.value)}
																	placeholder="IBAN"
																	component={Input}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										)}
										<Button
											disabled={!form.formState.isDirty}
											variant="secondary"
											className="h-14 rounded-xl">
											Daten speichern
										</Button>
									</form>
								</Form>
							)}
						</ResponsiveModalContent>
					</ResponsiveModal>

					<div className="mt-2 lg:mt-3 rounded-xl lg:rounded-[16px] px-3 py-4 lg:px-[18px] lg:py-5 bg-white flex flex-col">
						<h1 className="text-secondary text-xs lg:text-base font-extrabold">
							Aktualisiere dein Profil
						</h1>
						<div className="flex flex-col gap-4 mt-4 lg:mt-[29px]">
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex items-center gap-2 lg:gap-3">
								<img src={ProfilePhotoImage} className="size-8 rounded-full lg:size-16" />
								<div className="flex flex-col gap-1">
									<h1 className="font-extrabold max-lg:text-xs text-secondary">
										{capitalizeFirstLetter(auth.first_name) +
											' ' +
											capitalizeFirstLetter(auth.last_name)}
									</h1>
									<p className="text-secondary/50 max-lg:text-[10px] text-sm">
										Mitglied seit:{' '}
										{format(parseISO(auth.createdAt), 'dd. MMMM yyyy', { locale: de })}
									</p>
								</div>
							</div>
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex flex-col gap-8">
								<div className="flex items-center justify-between">
									<h1 className="text-secondary text-xs lg:text-base font-extrabold">
										Persönliche Daten
									</h1>
									<Button
										variant="outline"
										onClick={() => setIsModalVisible(1)}
										className="border-secondary/20 h-6 lg:h-8 rounded-[21px] text-secondary/50 text-[10px] lg:text-xs font-medium">
										<PencilSquareIcon className="size-3 lg:size-4 shrink-0" />
										Ändern
									</Button>
								</div>
								<div className="grid grid-cols-2 gap-y-8">
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Vorname</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{capitalizeFirstLetter(auth.first_name)}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Name</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{capitalizeFirstLetter(auth.last_name)}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Email</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">{auth.email}</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">
											Telefonnummer
										</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{auth.phone_number}
										</p>
									</div>
								</div>
							</div>
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex flex-col gap-8">
								<div className="flex items-center justify-between">
									<h1 className="text-secondary text-xs lg:text-base font-extrabold">Adresse</h1>
									<Button
										variant="outline"
										onClick={() => setIsModalVisible(2)}
										className="border-secondary/20 h-6 lg:h-8 rounded-[21px] text-secondary/50 text-[10px] lg:text-xs font-medium">
										<PencilSquareIcon className="size-3 lg:size-4 shrink-0" />
										Ändern
									</Button>
								</div>
								<div className="grid grid-cols-2 gap-y-8">
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Postleitzahl</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">{auth.zip}</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Ort</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{capitalizeFirstLetter(auth.country)}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Adresse</p>
										<p className="text-secondary capitalize text-xs lg:text-sm font-extrabold">
											{auth.address}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">IBAN</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{formatIban(auth.iban)}
										</p>
									</div>
								</div>
							</div>
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex flex-col gap-8">
								<h1 className="text-secondary text-xs lg:text-base font-extrabold">
									Empfehlungs-Code
								</h1>
								<div className="grid grid-cols-2 gap-y-8">
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium flex items-center gap-1 lg:gap-2">
											Empfehlungs-Code{' '}
											{!auth.referral_code && (
												<PlusCircleIcon
													onClick={() => generateReferralCode()}
													className="text-secondary size-4 lg:size-5 cursor-pointer"
												/>
											)}
										</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{auth.referral_code}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">
											Erfolgreiche Empfehlungen
										</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{referralCount}
										</p>
									</div>
								</div>
							</div>
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex flex-col gap-8">
								<div className="flex items-center justify-between">
									<h1 className="text-secondary text-xs lg:text-base font-extrabold">
										Passwort ändern
									</h1>
									<Button
										variant="outline"
										onClick={() => setIsModalVisible(3)}
										className="border-secondary/20 h-6 lg:h-8 rounded-[21px] text-secondary/50 text-[10px] lg:text-xs font-medium">
										<PencilSquareIcon className="size-3 lg:size-4 shrink-0" />
										Ändern
									</Button>
								</div>
								<div className="grid grid-cols-2 gap-y-8">
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Passwort</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">●●●●●●●●</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			) : (
				<Layout title="Mein Profil">
					<ResponsiveModal
						open={!!isModalVisible}
						onOpenChange={() => {
							form.reset()
							passwordForm.reset()
							form.clearErrors()
							passwordForm.clearErrors()
							setIsModalVisible(null)
						}}>
						<ResponsiveModalContent className="gap-0 !max-w-[550px]">
							<ResponsiveModalHeader className="max-lg:pb-2">
								<ResponsiveModalTitle className="mb-0 max-lg:text-left">
									{isModalVisible === 1
										? 'Persönliche Daten'
										: isModalVisible === 2
											? 'Adresse'
											: 'Passwort ändern'}
								</ResponsiveModalTitle>
								<ResponsiveModalDescription className="sr-only">
									Update profile
								</ResponsiveModalDescription>
							</ResponsiveModalHeader>
							{isModalVisible === 3 ? (
								<Form {...passwordForm}>
									<form
										onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
										className="flex flex-col gap-10">
										<div className="mt-10 flex flex-col gap-8">
											<FormField
												control={passwordForm.control}
												name="email"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">Email</FormLabel>
														<FormControl>
															<Input placeholder="Email" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
											<FormField
												control={passwordForm.control}
												name="oldPassword"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">Altes passwort</FormLabel>
														<FormControl>
															<Input placeholder="Altes passwort" type="password" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
											<FormField
												control={passwordForm.control}
												name="newPassword"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">Neues passwort</FormLabel>
														<FormControl>
															<Input placeholder="Neues passwort" type="password" {...field} />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<Button variant="secondary" className="h-14 rounded-xl">
											Daten speichern
										</Button>
									</form>
								</Form>
							) : (
								<Form {...form}>
									<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-10">
										{isModalVisible === 1 ? (
											<div className="mt-10 flex flex-col gap-8">
												<FormField
													control={form.control}
													name="first_name"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Name</FormLabel>
															<FormControl>
																<Input
																	placeholder="Name"
																	{...field}
																	value={capitalizeFirstLetter(field.value)}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="last_name"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Name</FormLabel>
															<FormControl>
																<Input
																	placeholder="Name"
																	{...field}
																	value={capitalizeFirstLetter(field.value)}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="email"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Email</FormLabel>
															<FormControl>
																<Input disabled placeholder="Email" {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="phone_number"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Telefonnummer</FormLabel>
															<FormControl>
																<Input placeholder="Telefonnummer" {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										) : (
											<div className="mt-10 flex flex-col gap-8">
												<FormField
													control={form.control}
													name="zip"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Postleitzahl</FormLabel>
															<FormControl>
																<Input placeholder="Postleitzahl" {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="country"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Ort</FormLabel>
															<FormControl>
																<Input placeholder="Ort" {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="address"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">Adresse</FormLabel>
															<FormControl>
																<Input
																	placeholder="Adresse"
																	{...field}
																	value={capitalizeFirstLetter(field.value)}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="iban"
													render={({ field }) => (
														<FormItem className="w-full">
															<FormLabel className="text-muted-foreground">
																IBAN <span className="text-destructive-foreground">*</span>
															</FormLabel>
															<FormControl>
																<InputMask
																	{...primaryIbanOptions}
																	value={field.value ?? ''}
																	onChange={e => field.onChange(e.target.value)}
																	placeholder="IBAN"
																	component={Input}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										)}
										<Button
											disabled={!form.formState.isDirty}
											variant="secondary"
											className="h-14 rounded-xl">
											Daten speichern
										</Button>
									</form>
								</Form>
							)}
						</ResponsiveModalContent>
					</ResponsiveModal>

					<div className="mt-2 lg:mt-3 rounded-xl lg:rounded-[16px] px-3 py-4 lg:px-[18px] lg:py-5 bg-white flex flex-col">
						<h1 className="text-secondary text-xs lg:text-base font-extrabold">
							Aktualisiere dein Profil
						</h1>
						<div className="flex flex-col gap-4 mt-4 lg:mt-[29px]">
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex items-center gap-2 lg:gap-3">
								<img src={ProfilePhotoImage} className="size-8 rounded-full lg:size-16" />
								<div className="flex flex-col gap-1">
									<h1 className="font-extrabold max-lg:text-xs text-secondary">
										{capitalizeFirstLetter(auth.first_name) +
											' ' +
											capitalizeFirstLetter(auth.last_name)}
									</h1>
									<p className="text-secondary/50 max-lg:text-[10px] text-sm">
										Mitglied seit:{' '}
										{format(parseISO(auth.createdAt), 'dd. MMMM yyyy', { locale: de })}
									</p>
								</div>
							</div>
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex flex-col gap-8">
								<div className="flex items-center justify-between">
									<h1 className="text-secondary text-xs lg:text-base font-extrabold">
										Persönliche Daten
									</h1>
									<Button
										variant="outline"
										onClick={() => setIsModalVisible(1)}
										className="border-secondary/20 h-6 lg:h-8 rounded-[21px] text-secondary/50 text-[10px] lg:text-xs font-medium">
										<PencilSquareIcon className="size-3 lg:size-4 shrink-0" />
										Ändern
									</Button>
								</div>
								<div className="grid grid-cols-2 gap-y-8">
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Vorname</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{capitalizeFirstLetter(auth.first_name)}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Name</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{capitalizeFirstLetter(auth.last_name)}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Email</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">{auth.email}</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">
											Telefonnummer
										</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{auth.phone_number}
										</p>
									</div>
								</div>
							</div>
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex flex-col gap-8">
								<div className="flex items-center justify-between">
									<h1 className="text-secondary text-xs lg:text-base font-extrabold">Adresse</h1>
									<Button
										variant="outline"
										onClick={() => setIsModalVisible(2)}
										className="border-secondary/20 h-6 lg:h-8 rounded-[21px] text-secondary/50 text-[10px] lg:text-xs font-medium">
										<PencilSquareIcon className="size-3 lg:size-4 shrink-0" />
										Ändern
									</Button>
								</div>
								<div className="grid grid-cols-2 gap-y-8">
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Postleitzahl</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">{auth.zip}</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Ort</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{capitalizeFirstLetter(auth.country)}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Adresse</p>
										<p className="text-secondary capitalize text-xs lg:text-sm font-extrabold">
											{auth.address}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">IBAN</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{formatIban(auth.iban)}
										</p>
									</div>
								</div>
							</div>
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex flex-col gap-8">
								<h1 className="text-secondary text-xs lg:text-base font-extrabold">
									Empfehlungs-Code
								</h1>
								<div className="grid grid-cols-2 gap-y-8">
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium flex items-center gap-1 lg:gap-2">
											Empfehlungs-Code{' '}
											{!auth.referral_code && (
												<PlusCircleIcon
													onClick={() => generateReferralCode()}
													className="text-secondary size-4 lg:size-5 cursor-pointer"
												/>
											)}
										</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{auth.referral_code}
										</p>
									</div>
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">
											Erfolgreiche Empfehlungen
										</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">
											{referralCount}
										</p>
									</div>
								</div>
							</div>
							<div className="border border-[#EEEEEE] rounded-xl lg:rounded-[16px] p-3 lg:p-6 flex flex-col gap-8">
								<div className="flex items-center justify-between">
									<h1 className="text-secondary text-xs lg:text-base font-extrabold">
										Passwort ändern
									</h1>
									<Button
										variant="outline"
										onClick={() => setIsModalVisible(3)}
										className="border-secondary/20 h-6 lg:h-8 rounded-[21px] text-secondary/50 text-[10px] lg:text-xs font-medium">
										<PencilSquareIcon className="size-3 lg:size-4 shrink-0" />
										Ändern
									</Button>
								</div>
								<div className="grid grid-cols-2 gap-y-8">
									<div className="flex flex-col gap-1 lg:gap-3">
										<p className="text-secondary/50 text-xs lg:text-sm font-medium">Passwort</p>
										<p className="text-secondary text-xs lg:text-sm font-extrabold">●●●●●●●●</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Layout>
			)}
		</>
	)
}
